import axios from 'axios';

// Use environment variable or default to localhost
// For network access, set REACT_APP_API_URL in .env file
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
  changePassword: (passwordData) => api.put('/auth/password', passwordData),
  deleteAccount: () => api.delete('/auth/account'),
  toggleOTP: (data) => api.put('/auth/toggle-otp', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// OTP APIs
export const otpAPI = {
  send: (data) => api.post('/otp/send', data),
  verify: (data) => api.post('/otp/verify', data),
  resend: (data) => api.post('/otp/resend', data),
};

// Products APIs
export const productsAPI = {
  getAll: () => api.get('/products'),
  getById: (id) => api.get(`/products/${id}`),
  create: (productData) => api.post('/products', productData),
  update: (id, productData) => api.put(`/products/${id}`, productData),
  delete: (id) => api.delete(`/products/${id}`),
  getCategories: () => api.get('/products/categories/all'),
};

// Events APIs
export const eventsAPI = {
  getAll: () => api.get('/events'),
  getById: (id) => api.get(`/events/${id}`),
  create: (eventData) => api.post('/events', eventData),
  update: (id, eventData) => api.put(`/events/${id}`, eventData),
  delete: (id) => api.delete(`/events/${id}`),
};

// Feedback APIs
export const feedbackAPI = {
  getApproved: () => api.get('/feedback'),
  getAll: () => api.get('/feedback/all'),
  submit: (feedbackData) => api.post('/feedback', feedbackData),
  approve: (id, status) => api.put(`/feedback/${id}/approve`, { is_approved: status }),
  delete: (id) => api.delete(`/feedback/${id}`),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (search) => api.get('/admin/users', { params: { search } }),
  toggleUserStatus: (id) => api.put(`/admin/users/${id}/toggle-status`),
  getFeedback: () => api.get('/admin/feedback'),
  approveFeedback: (id, status) => api.put(`/admin/feedback/${id}/approve`, { is_approved: status }),
  deleteFeedback: (id) => api.delete(`/admin/feedback/${id}`),
  getProducts: () => api.get('/admin/products'),
  createProduct: (productData) => api.post('/admin/products', productData),
  updateProduct: (id, productData) => api.put(`/admin/products/${id}`, productData),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  getEvents: () => api.get('/admin/events'),
  createEvent: (eventData) => api.post('/admin/events', eventData),
  updateEvent: (id, eventData) => api.put(`/admin/events/${id}`, eventData),
  deleteEvent: (id) => api.delete(`/admin/events/${id}`),
  getImageLibrary: () => api.get('/admin/image-library'),
  uploadToLibrary: (imageData) => api.post('/admin/image-library', imageData),
  deleteFromLibrary: (id) => api.delete(`/admin/image-library/${id}`),
  getCertificates: () => api.get('/certificates/admin'),
  createCertificate: (certData) => api.post('/certificates', certData),
  updateCertificate: (id, certData) => api.put(`/certificates/${id}`, certData),
  deleteCertificate: (id) => api.delete(`/certificates/${id}`),
};

// Certificates APIs (Public)
export const certificatesAPI = {
  getAll: () => api.get('/certificates'),
  search: (query) => api.get('/certificates/search', { params: { query } }),
};

export default api;
